import { Visibility, VisibilityOff, Lock, Person, Business, MailOutline } from '@mui/icons-material'
import {
  Typography,
  TextField,
  Grid,
  Box,
  IconButton,
  CardContent,
  Card,
  Chip,
  Stack,
  InputAdornment,
  Button,
  MenuItem,
} from '@mui/material'
import { StatusCodes } from 'http-status-codes'
import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthService, LocalStorageToken } from '../../interactors/services/AuthService'

import Background from './login-bg.png'
import placeholder from './login-bg-compressed.png'
import { useProgressiveImage } from '../../utils/hooks/useProgressiveImage'
import { ORGANIZATION_NUMBER_ERROR, parseOrganizationNumber } from '../../domain/Customer/organizationNumber'
import { useAccountsStore } from '../../store/AccountsStore'
import { LoadingButton } from '@mui/lab'
import { blue, colors } from '../theme'

import { LEGACY_INTERFACE_ORIGIN } from '../../utils/legacyInterface'

import { RequestResetPasswordPrompt } from './RequestResetPasswordPrompt'
import { Controller, useForm } from 'react-hook-form'
import {
  AccountDtoPermission,
  AccountDtoRoles,
  AnimalDtoSpecies,
  AnimalDtoSpecies as AnimalSpeciesEnum,
} from '../../interactors/gen/backendClient'
import { CustomersClient } from '../../interactors/clients/CustomerClient'
import { getEmojiSpecies, getReadableSpecies } from '../../domain/Animal/AnimalDisplay'
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input'

type AuthView = 'login' | 'signup'

type CreateAssociationFormValues = {
  associationName: string
  postalCode: string
  SIRETNumber: string
  species: AnimalDtoSpecies[]
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  password: string
  confirmPassword: string
}

const INVALID_PHONE_NUMBER_MESSAGE = "Le numéro de téléphone n'est pas valide."
const PHONE_NUMBER_HELPER_TEXT = 'Ex. +33 (0)6 12 34 56 78'

const normalizeFrenchPhoneNumber = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined
  }

  const compactValue = value.trim().replace(/\s+/g, '')
  if (compactValue === '' || compactValue === '+33' || compactValue === '0') {
    return undefined
  }

  if (compactValue.startsWith('+330')) {
    return `+33${compactValue.slice(4)}`
  }

  if (compactValue.startsWith('00330')) {
    return `+33${compactValue.slice(5)}`
  }

  if (compactValue.startsWith('0033')) {
    return `+${compactValue.slice(2)}`
  }

  if (compactValue.startsWith('0') && !compactValue.startsWith('00')) {
    return `+33${compactValue.slice(1)}`
  }

  return compactValue
}

const validateFrenchPhoneNumber = (value?: string): true | string => {
  const normalizedPhoneNumber = normalizeFrenchPhoneNumber(value)

  return normalizedPhoneNumber && matchIsValidTel(normalizedPhoneNumber) ? true : INVALID_PHONE_NUMBER_MESSAGE
}

const normalizeFrenchPhoneNumberInput = (value: string, countryCode: string | null): string => {
  if (countryCode !== 'FR') {
    return value
  }

  const digitsOnly = value.replace(/\D/g, '')
  if (digitsOnly === '') {
    return '+33'
  }

  if (digitsOnly.startsWith('0033')) {
    return `+${digitsOnly.slice(2)}`
  }

  if (digitsOnly.startsWith('33')) {
    return `+${digitsOnly}`
  }

  if (digitsOnly.startsWith('0')) {
    return `+33${digitsOnly}`
  }

  if (digitsOnly.startsWith('6') || digitsOnly.startsWith('7')) {
    return `+33${digitsOnly}`
  }

  return `+33${digitsOnly}`
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const accountsStore = useAccountsStore()

  const [authView, setAuthView] = useState<AuthView>('login')
  const [isLoading, setIsLoading] = useState(false)
  const [isSignupLoading, setIsSignupLoading] = useState(false)
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = useState('')
  const [serverError, setServerError] = useState('')
  const [signupError, setSignupError] = useState('')
  const [signupSuccess, setSignupSuccess] = useState('')
  const [isPasswordField, setIsPasswordField] = useState(true)
  const [isSignupPasswordField, setIsSignupPasswordField] = useState(true)
  const [isSignupConfirmPasswordField, setIsSignupConfirmPasswordField] = useState(true)
  const [isSpeciesMenuOpen, setIsSpeciesMenuOpen] = useState(false)
  const speciesMenuCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const speciesOptions = Object.values(AnimalSpeciesEnum).map((species) => ({
    emoji: getEmojiSpecies(species),
    label: getReadableSpecies(species),
    value: species,
  }))

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateAssociationFormValues>({
    defaultValues: {
      associationName: '',
      postalCode: '',
      SIRETNumber: '',
      species: [],
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '+33',
      password: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  })

  const signupPassword = watch('password')

  const clearSpeciesMenuCloseTimeout = () => {
    if (speciesMenuCloseTimeoutRef.current) {
      clearTimeout(speciesMenuCloseTimeoutRef.current)
      speciesMenuCloseTimeoutRef.current = null
    }
  }

  const scheduleSpeciesMenuClose = () => {
    clearSpeciesMenuCloseTimeout()
    speciesMenuCloseTimeoutRef.current = setTimeout(() => {
      setIsSpeciesMenuOpen(false)
      speciesMenuCloseTimeoutRef.current = null
    }, 1000)
  }

  useEffect(() => {
    if (AuthService.isLogged()) {
      navigate('/')
    }
  })

  useEffect(
    () => () => {
      clearSpeciesMenuCloseTimeout()
    },
    []
  )

  const handleLogin = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    setIsLoading(true)
    setServerError('')

    await AuthService.loginWithPassword(email.trim(), password.trim())
      .then(() => {
        if (AuthService.account) {
          accountsStore.changeConnectedAccount(AuthService.account)
          navigate('/')
          return
        }

        if (LocalStorageToken.getCurrentToken()) {
          window.location.assign('/')
          return
        }
      })
      .catch((error) => {
        console.error({ error })
        if (!error.response) {
          setServerError(
            'Impossible de joindre le serveur. Vérifiez votre connexion ou réessayez dans quelques instants.'
          )
          return
        }

        if (error.response.status === StatusCodes.UNAUTHORIZED) {
          setServerError('Mot de passe incorrect.')
        } else {
          if (error.response.data?.message === 'User is silent') {
            setServerError(
              `Votre compte n'a pas les permissions nécessaires pour accéder à l'application. Si vous pensez que c'est une erreur, contactez un administrateur de votre association`
            )
          } else {
            setServerError('Something wrong happened')
          }
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleCreateAssociation = async (data: CreateAssociationFormValues) => {
    setIsSignupLoading(true)
    setSignupError('')
    setSignupSuccess('')

    const normalizedPhoneNumber = normalizeFrenchPhoneNumber(data.phoneNumber)
    if (!normalizedPhoneNumber) {
      setSignupError(INVALID_PHONE_NUMBER_MESSAGE)
      setIsSignupLoading(false)
      return
    }

    try {
      await CustomersClient.createCustomerSpace({
        customer: {
          name: data.associationName.trim(),
          postalCode: data.postalCode.trim(),
          // Stored under whichever number it is: the form asks once, since an organization holds a
          // SIRET or an RNA, never both. The field is validated above, so this cannot be null here.
          ...(parseOrganizationNumber(data.SIRETNumber) ?? {}),
          preferences: {
            species: data.species,
          },
        },
        adminAccounts: [
          {
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: data.email.trim(),
            phoneNumber: normalizedPhoneNumber,
            password: data.password,
            roles: [AccountDtoRoles.Member],
            permission: AccountDtoPermission.Administrator,
          },
        ],
      })

      try {
        await AuthService.loginWithPassword(data.email.trim(), data.password)

        if (AuthService.account) {
          accountsStore.changeConnectedAccount(AuthService.account)
          reset()
          navigate('/')
          return
        }

        if (LocalStorageToken.getCurrentToken()) {
          window.location.assign('/')
          return
        }
      } catch (loginError) {
        console.error({ loginError })
        setSignupSuccess(
          "Le compte association a été créé, mais la connexion automatique a échoué. Vous pouvez vous connecter avec l'email administrateur."
        )
        setEmail(data.email.trim())
        setPassword('')
        setAuthView('login')
        reset()
      }
    } catch (error: unknown) {
      const errorResponse = error as { response?: { data?: { message?: string; reason?: string } } }
      const backendMessage =
        errorResponse.response?.data?.message ||
        errorResponse.response?.data?.reason ||
        'La création du compte a échoué.'
      setSignupError(backendMessage)
    } finally {
      setIsSignupLoading(false)
    }
  }

  const loadedBackground = useProgressiveImage(Background)

  const authCardSx = {
    width: { md: '100%', xs: '600px' },
    maxWidth: { md: '600px', xs: '95vw' },
    borderRadius: 4,
    border: '1px solid #E4E8F2',
    boxShadow: '0 18px 50px rgba(32, 56, 100, 0.14)',
    overflow: 'hidden',
  }

  const viewButtonSx = (isActive: boolean) => ({
    flex: 1,
    minHeight: 42,
    borderRadius: '999px',
    textTransform: 'none',
    fontWeight: 700,
    color: isActive ? colors.cello : '#7A8197',
    backgroundColor: isActive ? 'white' : 'transparent',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: isActive ? 'white' : 'rgba(255,255,255,0.45)',
      boxShadow: 'none',
    },
  })

  const formFieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: '0.92rem',
    },
    '& .MuiInputBase-input::placeholder': {
      fontSize: '0.92rem',
    },
    '& .MuiOutlinedInput-root': {
      minHeight: 44,
      borderRadius: 3,
      backgroundColor: '#fff',
    },
  }

  const speciesChipSx = {
    borderRadius: '999px',
    backgroundColor: '#EEF2F8',
    color: colors.cello,
    fontWeight: 700,
    '& .MuiChip-label': {
      px: 1.25,
    },
  }

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #FFF5D6 0%, #F9FBFF 45%, #EAF1FB 100%)',
        display: 'flex',
        flexDirection: { md: 'row', xs: 'column' },
        justifyContent: 'center',
        alignItems: { md: 'center', xs: 'stretch' },
        minHeight: '100vh',
        width: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      <Box sx={{ height: authView === 'signup' ? 0 : { md: 0, sm: 100, xs: 200 } }}></Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { md: 'center', xs: authView === 'signup' ? 'flex-start' : 'center' },
          alignItems: 'center',
          height: '100%',
          minHeight: { sm: authView === 'signup' ? 'unset' : '450px', xs: authView === 'signup' ? 'unset' : '450px' },
          width: { md: '50%', xs: '100%' },
          pt: { xs: authView === 'signup' ? 2 : 0, md: authView === 'signup' ? 5 : 0 },
          pb: { xs: authView === 'signup' ? 2 : 0, md: authView === 'signup' ? 7 : 0 },
        }}
      >
        <Box
          flexGrow={authView === 'signup' ? 0 : 1}
          margin={{ md: 'auto', xs: authView === 'signup' ? 0 : 'auto' }}
          padding="0px 2%"
          boxSizing="border-box"
          paddingLeft={{ lg: 10 }}
          width="100%"
        >
          <div>
            <Box
              sx={{
                margin: 'auto',
                '@media orientation: portrait': {
                  maxWidth: { md: '600px', sm: '80vh' },
                },
                '@media orientation: landscape': {
                  maxWidth: { md: '600px', sm: authView === 'signup' ? '100%' : '80vw' },
                },
                maxWidth: { md: '600px', xs: '100%' },
                color: colors.cello,
                marginTop: { md: authView === 'signup' ? 5 : 14, xs: authView === 'signup' ? 0 : 5 },
                marginBottom: authView === 'signup' ? 1.5 : 0,
                width: { md: '100%', sm: '600px', xs: '100%' },
              }}
            >
              {authView === 'login' && (
                <>
                  <Typography
                    variant="h1"
                    sx={{ color: blue, fontFamily: 'Insaniburger', fontSize: { xs: '80px', sm: '100px' } }}
                  >
                    Petso
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: blue,
                      fontWeight: 700,
                      fontSize: { xs: '1.1rem', sm: '1.55rem' },
                      marginBottom: 2,
                    }}
                  >
                    Outil conçu pour les associations et les refuges pour faciliter la prise en charge et
                    l&apos;adoption animale
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card elevation={0} sx={authCardSx}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.75,
                      p: 0.75,
                      mb: 2.5,
                      borderRadius: '999px',
                      backgroundColor: '#F2F5FA',
                    }}
                  >
                    <Button type="button" onClick={() => setAuthView('login')} sx={viewButtonSx(authView === 'login')}>
                      Se connecter
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setAuthView('signup')}
                      sx={viewButtonSx(authView === 'signup')}
                    >
                      Créer un compte association
                    </Button>
                  </Box>

                  {authView === 'login' ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ color: colors.cello, fontSize: '1.55rem', fontWeight: 800, lineHeight: 1.1 }}>
                          Connexion
                        </Typography>
                        <Typography sx={{ color: '#7A8197', fontSize: '0.92rem', mt: 0.75 }}>
                          Connectez-vous pour retrouver votre espace Petso.
                        </Typography>
                        {signupSuccess && (
                          <Typography sx={{ color: '#2F7D32', fontSize: '0.9rem', fontWeight: 700, mt: 1.25 }}>
                            {signupSuccess}
                          </Typography>
                        )}
                      </Box>

                      <form onSubmit={handleLogin}>
                        <Stack spacing={2}>
                          <TextField
                            fullWidth
                            label="Email"
                            value={email}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                            sx={formFieldSx}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Person />
                                </InputAdornment>
                              ),
                            }}
                          />

                          <TextField
                            fullWidth
                            type={isPasswordField ? 'password' : 'text'}
                            label="Mot de passe"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={formFieldSx}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    color="default"
                                    size="small"
                                    edge="end"
                                    onClick={() => setIsPasswordField(!isPasswordField)}
                                  >
                                    {isPasswordField ? <Visibility /> : <VisibilityOff />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />

                          {serverError && <Typography color="error">{serverError}</Typography>}

                          <LoadingButton
                            variant="contained"
                            id="login-button"
                            type="submit"
                            loading={isLoading}
                            sx={{
                              minHeight: 46,
                              borderRadius: '999px',
                              textTransform: 'none',
                              fontWeight: 800,
                              boxShadow: 'none',
                            }}
                          >
                            Se connecter
                          </LoadingButton>
                        </Stack>
                      </form>

                      <Box display="flex" justifyContent="center" alignItems="center" mt={1.5}>
                        <RequestResetPasswordPrompt />
                      </Box>

                      <Box display="flex" justifyContent="center" alignItems="center">
                        <Button
                          component="a"
                          href={`${LEGACY_INTERFACE_ORIGIN}/login`}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ textTransform: 'none', color: '#7A8197', fontSize: '0.85rem' }}
                        >
                          Accéder à l&apos;ancienne interface
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box sx={{ mb: 2.5 }}>
                        <Typography
                          sx={{ color: '#7A8197', fontSize: '1rem', fontWeight: 700, mb: 0.5 }}
                          component="h3"
                        >
                          Président.e d&apos;une APA ?
                        </Typography>
                        <Typography
                          sx={{
                            color: colors.cello,
                            fontSize: { xs: '1.35rem', sm: '1.65rem' },
                            fontWeight: 900,
                            lineHeight: 1.05,
                          }}
                          component="h1"
                        >
                          Créer votre compte association gratuitement
                        </Typography>
                      </Box>

                      <form onSubmit={handleSubmit(handleCreateAssociation)}>
                        <Stack spacing={2}>
                          <Box>
                            <Chip
                              label="Association"
                              sx={{
                                mb: 1.25,
                                borderRadius: '999px',
                                backgroundColor: '#EEF2F8',
                                color: colors.cello,
                                fontWeight: 800,
                              }}
                            />
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="Nom de l'association"
                                  sx={formFieldSx}
                                  error={!!errors.associationName}
                                  helperText={errors.associationName?.message}
                                  {...register('associationName', { required: "Le nom de l'association est requis." })}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <Business />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Code postal"
                                  sx={formFieldSx}
                                  error={!!errors.postalCode}
                                  helperText={errors.postalCode?.message}
                                  {...register('postalCode', { required: 'Le code postal est requis.' })}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Numéro SIRET ou RNA"
                                  sx={formFieldSx}
                                  error={!!errors.SIRETNumber}
                                  helperText={
                                    errors.SIRETNumber?.message ?? 'SIRET à 14 chiffres, ou RNA au format W123456789.'
                                  }
                                  {...register('SIRETNumber', {
                                    required: 'Le numéro SIRET ou RNA est requis.',
                                    validate: (value) =>
                                      parseOrganizationNumber(value) !== null || ORGANIZATION_NUMBER_ERROR,
                                  })}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Controller
                                  name="species"
                                  control={control}
                                  rules={{
                                    validate: (value) =>
                                      value.length > 0 ? true : 'Sélectionnez au moins une espèce.',
                                  }}
                                  render={({ field }) => (
                                    <TextField
                                      select
                                      fullWidth
                                      SelectProps={{
                                        multiple: true,
                                        open: isSpeciesMenuOpen,
                                        onOpen: () => {
                                          clearSpeciesMenuCloseTimeout()
                                          setIsSpeciesMenuOpen(true)
                                        },
                                        onClose: () => {
                                          clearSpeciesMenuCloseTimeout()
                                          setIsSpeciesMenuOpen(false)
                                        },
                                        value: field.value,
                                        onChange: (event) => {
                                          field.onChange(event.target.value as AnimalDtoSpecies[])
                                          scheduleSpeciesMenuClose()
                                        },
                                        renderValue: (selected) => (
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, py: 0.25 }}>
                                            {(selected as AnimalDtoSpecies[]).map((specie) => {
                                              const option = speciesOptions.find((item) => item.value === specie)

                                              return (
                                                <Chip
                                                  key={specie}
                                                  label={`${option?.emoji || ''} ${option?.label || specie}`.trim()}
                                                  sx={speciesChipSx}
                                                />
                                              )
                                            })}
                                          </Box>
                                        ),
                                        MenuProps: {
                                          PaperProps: {
                                            sx: {
                                              mt: 1,
                                              borderRadius: 3,
                                              border: '1px solid #E4E8F2',
                                              boxShadow: '0 18px 40px rgba(32, 56, 100, 0.12)',
                                            },
                                          },
                                        },
                                      }}
                                      label="Espèces prises en charge"
                                      sx={{
                                        ...formFieldSx,
                                        '& .MuiSelect-select': {
                                          display: 'flex',
                                          alignItems: 'center',
                                          flexWrap: 'wrap',
                                          gap: 0.5,
                                        },
                                      }}
                                      error={!!errors.species}
                                      helperText={errors.species?.message}
                                    >
                                      {speciesOptions.map((option) => (
                                        <MenuItem
                                          key={option.value}
                                          value={option.value}
                                          sx={{
                                            borderRadius: 2,
                                            mx: 0.75,
                                            my: 0.25,
                                            minHeight: 44,
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <Box sx={{ fontSize: '1.1rem', lineHeight: 1 }}>{option.emoji}</Box>
                                            <Typography sx={{ color: colors.cello, fontWeight: 700 }}>
                                              {option.label}
                                            </Typography>
                                          </Box>
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  )}
                                />
                              </Grid>
                            </Grid>
                          </Box>

                          <Box sx={{ pt: 0.5 }}>
                            <Chip
                              label="Compte administrateur"
                              sx={{
                                mb: 1.25,
                                borderRadius: '999px',
                                backgroundColor: '#FFF5D6',
                                color: colors.cello,
                                fontWeight: 800,
                              }}
                            />
                            <Typography sx={{ color: '#7A8197', fontSize: '0.92rem', mt: -0.25, mb: 1.5 }}>
                              Invitez ensuite d&apos;autres membres et familles d&apos;accueil.
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Prénom"
                                  sx={formFieldSx}
                                  error={!!errors.firstName}
                                  helperText={errors.firstName?.message}
                                  {...register('firstName', { required: 'Le prénom est requis.' })}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Nom"
                                  sx={formFieldSx}
                                  error={!!errors.lastName}
                                  helperText={errors.lastName?.message}
                                  {...register('lastName', { required: 'Le nom est requis.' })}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Email"
                                  sx={formFieldSx}
                                  error={!!errors.email}
                                  helperText={errors.email?.message}
                                  {...register('email', {
                                    required: "L'email est requis.",
                                    pattern: {
                                      value: /\S+@\S+\.\S+/,
                                      message: "L'email n'est pas valide.",
                                    },
                                  })}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <MailOutline />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Controller
                                  name="phoneNumber"
                                  control={control}
                                  rules={{
                                    validate: validateFrenchPhoneNumber,
                                  }}
                                  render={({ field, fieldState }) => (
                                    <MuiTelInput
                                      {...field}
                                      fullWidth
                                      defaultCountry="FR"
                                      forceCallingCode
                                      label="Téléphone"
                                      value={field.value || '+33'}
                                      onChange={(value, info) =>
                                        field.onChange(normalizeFrenchPhoneNumberInput(value, info.countryCode))
                                      }
                                      sx={formFieldSx}
                                      error={fieldState.invalid}
                                      helperText={fieldState.error?.message || PHONE_NUMBER_HELPER_TEXT}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Mot de passe"
                                  type={isSignupPasswordField ? 'password' : 'text'}
                                  sx={formFieldSx}
                                  error={!!errors.password}
                                  helperText={errors.password?.message}
                                  {...register('password', {
                                    required: 'Le mot de passe est requis.',
                                    validate: (value) =>
                                      value.length >= 8 || 'Le mot de passe doit contenir au moins 8 caractères.',
                                  })}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          size="small"
                                          edge="end"
                                          onClick={() => setIsSignupPasswordField(!isSignupPasswordField)}
                                        >
                                          {isSignupPasswordField ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Confirmer votre mot de passe"
                                  type={isSignupConfirmPasswordField ? 'password' : 'text'}
                                  sx={formFieldSx}
                                  error={!!errors.confirmPassword}
                                  helperText={errors.confirmPassword?.message}
                                  {...register('confirmPassword', {
                                    required: 'La confirmation du mot de passe est requise.',
                                    validate: (value) =>
                                      value === signupPassword || 'Les mots de passe ne correspondent pas.',
                                  })}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          size="small"
                                          edge="end"
                                          onClick={() => setIsSignupConfirmPasswordField(!isSignupConfirmPasswordField)}
                                        >
                                          {isSignupConfirmPasswordField ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>

                          {signupError && <Typography color="error">{signupError}</Typography>}

                          <LoadingButton
                            variant="contained"
                            type="submit"
                            loading={isSignupLoading}
                            sx={{
                              mt: 1,
                              minHeight: 46,
                              borderRadius: '999px',
                              textTransform: 'none',
                              fontWeight: 900,
                              boxShadow: 'none',
                            }}
                          >
                            Créer votre compte association
                          </LoadingButton>
                        </Stack>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>
          </div>
        </Box>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: { md: 'flex', xs: authView === 'signup' ? 'none' : 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: { md: '100vh', xs: '100%' },
          width: { md: '50%', xs: '100%' },
          backgroundImage: `url(${loadedBackground || placeholder})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: { md: 'left', xs: 'top' },
        }}
      ></Box>
    </Box>
  )
}
